/**
 * ExploreOverlay — the home screen shown over the free explore camera.
 *
 * No dimming (world renders full brightness). Two pieces: the marker-tracking Play button
 * (below) and the ExplorePanel card at the bottom. Play is entered via the marker, not a menu:
 * tap the ground to place a spawn marker, then tap Play to spawn there. All of it fades out on
 * entering play.
 */

import { useState, useEffect, useRef } from 'react';
import { Play, ZoomIn, Loader2 } from 'lucide-react';
import { useGameStore } from '../state/store';
import { GameMode } from '@worldify/shared';
import { getCamera } from '../game/scene/camera';
import { isTouch } from '../game/deviceMode';
import { useIsDesktopLayout } from './useDeviceMode';
import { controls } from '../game/player/controls';
import {
  tweenExploreDistance, getExploreEffectiveDistance, setExploreViewShiftY,
} from '../game/scene/ExploreCamera';
import { requestFullscreen } from './fullscreen';
import { ExplorePanel } from './ExplorePanel';
import { BUTTON_SURFACE, MARK_GRADIENT_CSS, MOTION } from './kit';
import { isMarkerPlaced, getMarkerBase, armMarkerSpawn } from '../game/spawn/SpawnMarker';
import { markerButtonState, type MarkerButtonState } from './markerButtonState';
/** Fixed on-screen height (px) of the line connecting the ground marker to the Play button. */
const MARKER_LINE_PX = 88;

const LABELS: Record<MarkerButtonState, string> = {
  play: 'Play from here',
  zoom: 'Zoom in to play',
  loading: 'Loading terrain at marker',
};

/** Diameter of the marker button — must match the `w-16 md:w-[72px]` classes below (and so it is
 *  keyed off the same `md` breakpoint, not off isTouch()). */
const BUTTON_PX_SMALL = 64;
const BUTTON_PX_MD = 72;

/**
 * Target orbit distance when the user taps the "zoom in" affordance while exploring at a coarse
 * base LOD. Well above MIN_DISTANCE (6 m) and ~2 chunk widths horizontal of the target at the
 * default 45° pitch — close enough that lod 0 is displayed immediately and the marker chunk
 * is comfortably in frame, far enough that the camera isn't parked right on top of (or
 * grazing the edge of) the lod-0 chunk column.
 */
const ZOOM_IN_DISTANCE = 22;

/**
 * Play button anchored to the spawn marker in screen space. The button + the vertical line
 * beneath it are pure fixed-pixel UI: we project only the marker's ground point and draw a
 * constant-height line up to the button, so the gap never changes with camera zoom (the old
 * 3D line grew/shrank with zoom). The button rides the top of that line.
 *
 * One fixed-size circle in three icon states — identical geometry throughout, so the control
 * never resizes or shifts on the marker line as it advances. Styled as the card's floating
 * sibling: the logo mark's green gradient behind white glyphs, ringed by a white border the same
 * 3 px as the stem below it.
 *   • play → the play glyph. Tapping spawns at the marker.
 *   • zoom → ZoomIn. Tapping tweens the orbit distance into the lod-0 base range (cosine
 *     ease-in-out over ~600 ms — the instant snap read as a teleport and made the marker feel
 *     like it flew off the screen). Lands at 22 m so the camera is comfortably outside the
 *     marker chunk column rather than grazing its edge.
 *   • loading → spinning Loader2, the same surface faded slightly. STILL TAPPABLE (it re-issues
 *     the zoom-in), so the control is never inert even if the streamer were wedged.
 *
 * Which one is `markerButtonState` — a pure function of (baseLod, distance, canPlay), recomputed
 * in the tick below. It is a separate tested module because this button used to get stuck: see the
 * note there before adding any latched state here.
 */
function MarkerPlayButton() {
  const ref = useRef<HTMLDivElement>(null);
  const touch = isTouch();
  const md = useIsDesktopLayout();
  // SPAWN GATE (two parts), read in the tick rather than subscribed — all three inputs to the
  // button's state are sampled from one place, every frame, so they can never disagree.
  //   • spawnReady: lod-0 data resident + ground under the spawn meshed + provable sky
  //     (VoxelWorld.isSpawnReadyAt, pushed per frame by GameCore.updateExploreMode).
  //   • spawnLod0Visible: the chunk is actually contributing pixels right now — geometry baked
  //     AND shown by the batch / preview (VoxelWorld.isChunkDisplayedAt).
  //     A chunk can be MeshedComplete and still be hidden by the walk, which looks
  //     like a hole from the user's perspective. canPlay waits for the audit-truth, not just
  //     the meshing-truth, so we never advertise Play over a chunk the user can't see.
  const [state, setState] = useState<MarkerButtonState>('loading');

  // THE ASSEMBLY IS CENTRED, NOT THE MARKER. Button + stem hang ABOVE the marker, so with the
  // marker on the screen's centre line the whole thing reads high. Asking the camera to sit the
  // world half an assembly low centres [button top … marker] in the viewport instead.
  useEffect(() => {
    const buttonPx = md ? BUTTON_PX_MD : BUTTON_PX_SMALL;
    setExploreViewShiftY((MARKER_LINE_PX + buttonPx) / 2);
    return () => setExploreViewShiftY(0);
  }, [md]);

  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = ref.current;
      const cam = getCamera();
      if (el && cam && isMarkerPlaced()) {
        const p = getMarkerBase().clone().project(cam);
        const onScreen = p.z < 1 && p.x >= -1 && p.x <= 1 && p.y >= -1 && p.y <= 1;
        if (onScreen) {
          el.style.display = '';
          el.style.left = `${(p.x * 0.5 + 0.5) * window.innerWidth}px`;
          el.style.top = `${(-p.y * 0.5 + 0.5) * window.innerHeight}px`;
        } else {
          el.style.display = 'none';
        }
      } else if (el) {
        el.style.display = 'none';
      }
      const s = useGameStore.getState();
      setState(markerButtonState(
        s.voxelStats.baseLod,
        getExploreEffectiveDistance(),
        s.spawnReady && s.spawnLod0Visible,
      ));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const play = async () => {
    // THE SPAWN GATE. Coarse terrain is drawn wherever lod-0 has not arrived, so the marker can sit
    // on ground that looks solid while the physics engine — lod-0 only — knows nothing about it.
    // Entering play there does not put the player on that ground: they freeze in mid-air over it.
    // Both halves must hold: the chunk has to be resident+meshed (spawnReady) AND actually
    // contributing pixels (spawnLod0Visible), otherwise we can play into a hole.
    const s = useGameStore.getState();
    if (!s.spawnReady || !s.spawnLod0Visible) return;
    armMarkerSpawn();
    if (touch) {
      // Mobile: enter fullscreen FIRST and wait for it, so the browser finishes the
      // fullscreen animation before the play-mode camera transition begins (no black flash).
      await requestFullscreen();
      useGameStore.getState().setGameMode(GameMode.Playing);
      return;
    }
    // Desktop: lock the pointer within this gesture.
    controls.requestPointerLock();
    useGameStore.getState().setGameMode(GameMode.Playing);
  };

  // Play when playable, otherwise pull the camera into the lod-0 range. In the loading state that
  // re-issue is harmless, it keeps the tap from falling through to the canvas and re-placing the
  // marker, and it means the control always has an action even if the world never becomes playable.
  const press = (e: React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (state === 'play') void play();
    else tweenExploreDistance(ZOOM_IN_DISTANCE);
  };

  // Every state is the same circle (size, radius, ring weight) — only the icon and palette change,
  // so advancing zoom → loading → play never moves the hit target.
  const circle = `pointer-events-auto w-16 h-16 md:w-[72px] md:h-[72px] rounded-full flex items-center justify-center ${MOTION}`;
  // brightness rather than a background swap on hover: the fill is a gradient image, so a
  // background-color utility would sit behind it and never show.
  const active = `${circle} ${BUTTON_SURFACE} hover:brightness-110 active:brightness-95 cursor-pointer`;
  const gradientFill = { backgroundImage: MARK_GRADIENT_CSS };
  const iconSize = touch ? 30 : 34;

  // Stacked glyphs, one visible: cross-fading them keeps the state change from popping. All three
  // stay mounted, which is why the surface below is a single button.
  const glyph = (key: MarkerButtonState, node: React.ReactNode) => (
    <span
      className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-out ${
        state === key ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {node}
    </span>
  );

  // Anchored by its bottom-center to the projected ground point; the line's foot sits on the
  // marker and the button floats a fixed MARKER_LINE_PX above it.
  return (
    <div
      ref={ref}
      style={{ position: 'fixed', transform: 'translate(-50%,-100%)', display: 'none' }}
      className="pointer-events-none flex flex-col items-center"
    >
      {/* ONE button through all three states. The state only decides the glyph and the action, so
          the surface never re-mounts and the icons can cross-fade into each other. */}
      <button
        onPointerDown={press}
        className={`${active} ${state === 'loading' ? 'opacity-75' : 'opacity-100'}`}
        style={gradientFill}
        aria-label={LABELS[state]}
        title={LABELS[state]}
      >
        <span className="relative w-full h-full">
          {glyph('play', <Play size={iconSize} fill="currentColor" />)}
          {glyph('zoom', <ZoomIn size={iconSize} />)}
          {glyph('loading', <Loader2 size={iconSize} className="animate-spin" />)}
        </span>
      </button>
      {/* Fixed-height connector stem down to the ground marker (constant px at any zoom). */}
      <div style={{ width: 3, height: MARKER_LINE_PX, backgroundColor: '#fff' }} />
    </div>
  );
}

export function ExploreOverlay() {
  const gameMode = useGameStore((s) => s.gameMode);
  const exploreReady = useGameStore((s) => s.exploreReady);

  // Keep the explore UI mounted through its fade-out so it can animate when leaving to Playing;
  // unmount ~after the transition so it can't capture input.
  const [renderExploreUi, setRenderExploreUi] = useState(gameMode === GameMode.Explore);
  useEffect(() => {
    if (gameMode === GameMode.Explore) { setRenderExploreUi(true); return; }
    const t = setTimeout(() => setRenderExploreUi(false), 550);
    return () => clearTimeout(t);
  }, [gameMode]);

  // Render in Explore + Playing — Playing only to keep the explore UI mounted through its fade-out.
  if (gameMode !== GameMode.Explore && gameMode !== GameMode.Playing) return null;

  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {renderExploreUi && (
        /* Fades out on entering play and back in once the play→explore outro completes
           (exploreReady). Opacity-only on the layer so MarkerPlayButton's own marker-tracking
           transform is undisturbed. */
        <div className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ease-out ${exploreReady ? 'opacity-100' : 'opacity-0'}`}>
          <MarkerPlayButton />

          {/* Bottom-centre on mobile, left + vertically centred on desktop. Positioning lives on
              the outer div and the enter/exit slide on the inner one, so the two transforms don't
              have to be composed into one class. */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 md:bottom-auto md:top-1/2 md:left-6 md:translate-x-0 md:-translate-y-1/2"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className={`transition-transform duration-500 ease-out ${
              exploreReady ? 'translate-y-0 md:translate-x-0' : 'translate-y-8 md:translate-y-0 md:-translate-x-10'
            }`}>
              <ExplorePanel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
